/**
 * OnboardingShell — wraps every onboarding step page with a consistent
 * header (logo + progress bar + step pill) and a sign-out escape hatch.
 *
 * Steps in order:
 *   mail_otp · admin_verify · kra_accept · kpi_accept · training · welcome
 * (`complete` is unblocked and renders the regular app.)
 */
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Mail, ShieldCheck, FileText, Target, GraduationCap, Sparkles, LogOut } from 'lucide-react';
import AgeonLogo from '../../components/AgeonLogo';
import { useAuth } from '../../store/auth';
import api from '../../utils/api';

const STEPS = [
    { key: 'mail_otp', path: '/onboarding/verify-email', label: 'Verify email', icon: Mail },
    { key: 'admin_verify', path: '/onboarding/pending-approval', label: 'Admin review', icon: ShieldCheck },
    { key: 'kra_accept', path: '/onboarding/kra', label: 'Accept KRA', icon: FileText },
    { key: 'kpi_accept', path: '/onboarding/kpi', label: 'Accept KPI', icon: Target },
    { key: 'training', path: '/onboarding/training', label: 'Training', icon: GraduationCap },
    { key: 'welcome', path: '/onboarding/welcome', label: 'Welcome', icon: Sparkles },
];

export default function OnboardingShell() {
    const user = useAuth(s => s.user);
    const logout = useAuth(s => s.logout);
    const loc = useLocation();
    const [trainingEnabled, setTrainingEnabled] = useState(true);

    useEffect(() => {
        api.get('/training-content')
            .then(r => setTrainingEnabled(r.data?.hasContent !== false))
            .catch(() => setTrainingEnabled(true));
    }, []);

    const steps = useMemo(
        () => (trainingEnabled ? STEPS : STEPS.filter(s => s.key !== 'training')),
        [trainingEnabled],
    );

    const stepKey = user?.onboardingStep === 'training' && !trainingEnabled ? 'welcome' : user?.onboardingStep;
    const currentIdx = steps.findIndex(s => loc.pathname.startsWith(s.path));
    const userIdx = steps.findIndex(s => s.key === stepKey);
    const progress = Math.round(((Math.max(currentIdx, userIdx, 0) + 1) / steps.length) * 100);

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-b from-brand-ivory/40 via-white to-white text-brand-dark">
            <header className="shrink-0 z-20 bg-white/80 backdrop-blur border-b border-ink-100">
                <div className="max-w-3xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-4">
                    <AgeonLogo />
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-ink-500 hidden sm:inline">
                            Step {Math.max(currentIdx, userIdx, 0) + 1} of {steps.length}
                        </span>
                        <button onClick={logout} title="Sign out — your progress is saved" className="text-ink-500 hover:text-brand-red flex items-center gap-1 text-xs">
                            <LogOut className="h-4 w-4" /> Sign out
                        </button>
                    </div>
                </div>
                {/* progress bar */}
                <div className="h-1 bg-ink-100">
                    <div className="h-full bg-brand-teal transition-all" style={{ width: `${progress}%` }} />
                </div>
                {/* step pills */}
                <div className="max-w-3xl mx-auto px-4 lg:px-6 py-3 flex items-center gap-2 overflow-x-auto">
                    {steps.map((s, i) => {
                        const isCurrent = i === Math.max(currentIdx, userIdx, 0);
                        const isPast = i < userIdx;
                        return (
                            <div key={s.key} className={`shrink-0 flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-semibold transition
                                ${isCurrent ? 'bg-brand-teal text-white'
                                    : isPast ? 'bg-emerald-50 text-emerald-700'
                                        : 'bg-ink-100 text-ink-500'}`}>
                                <s.icon className="h-3 w-3" />
                                <span className="hidden sm:inline">{s.label}</span>
                                <span className="sm:hidden">{i + 1}</span>
                            </div>
                        );
                    })}
                </div>
            </header>

            <main className="flex-1 min-h-0 overflow-y-auto">
                <div className="max-w-3xl mx-auto px-4 lg:px-6 py-8 lg:py-12">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
